import React from 'react';
import { Star } from 'lucide-react';

/**
 * Reusable star rating component.
 * - When `onChange` is passed, becomes interactive (hover + click) for input.
 * - Otherwise renders read-only stars for `value` (0..5, supports halves visually).
 */
const StarRating = ({ value = 0, onChange, size = 18, className = '', testIdPrefix = 'star' }) => {
  const [hover, setHover] = React.useState(0);
  const interactive = typeof onChange === 'function';
  const displayValue = interactive ? (hover || value) : value;

  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`} role={interactive ? 'radiogroup' : 'img'} aria-label={`Rating ${value} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= Math.round(displayValue);
        const half = !filled && i - 0.5 <= displayValue;
        const color = filled || half ? 'text-yellow-500' : 'text-gray-300';
        const fill = filled ? 'currentColor' : 'none';
        return (
          <button
            key={i}
            type="button"
            disabled={!interactive}
            onMouseEnter={() => interactive && setHover(i)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange(i)}
            className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'} p-0.5`}
            data-testid={`${testIdPrefix}-${i}`}
            aria-label={`${i} star${i > 1 ? 's' : ''}`}
          >
            <Star size={size} className={color} fill={fill} strokeWidth={1.75} />
          </button>
        );
      })}
    </div>
  );
};

export default StarRating;
